import { CloseButton } from '../elements/CloseButton'
import React, { PropsWithChildren, ReactElement, useEffect } from 'react'
import { cn } from '../../utils'
import { useModals } from '../../store'
import { isElectron } from '../../utils/runtimeUtils'
import { motion } from 'framer-motion'
import { Portal } from 'react-portal'

type ModalProps = {
  id: string
  headerClassName?: string
  className?: string
  header?: string | ReactElement
  onClose?: () => void
  closeOnClick?: boolean
  closeOnEscape?: boolean
  overlayClassName?: string
  zIndex?: number
  secondary?: ReactElement
  secondaryClassName?: string
  secondaryOffsetPx?: number
  secondaryHeader?: string | ReactElement
  secondaryHeaderClassName?: string
}

export const Modal: React.FC<PropsWithChildren<ModalProps>> = ({
  id,
  children,
  className,
  headerClassName,
  header,
  onClose,
  closeOnClick,
  closeOnEscape,
  secondary,
  secondaryClassName,
  secondaryOffsetPx = 12,
  secondaryHeader,
  secondaryHeaderClassName,
}) => {
  const { closeModal } = useModals()

  const close = () => {
    closeModal(id)
    if (typeof onClose === 'function') {
      onClose()
    }
  }

  useEffect(() => {
    if (closeOnEscape === false) {
      return
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [closeOnEscape])

  return (
    <Portal>
      <motion.div
        className={cn(
          'absolute left-0 top-0 z-[11] flex h-full w-full flex-col items-center justify-center bg-black/80',
          !isElectron && 'pointer-events-auto fixed inset-0 z-[60]',
        )}
        onClick={(e) => {
          if (closeOnClick !== false) {
            e.stopPropagation()
            close()
          }
        }}
        {...OverlayAnimation}
      >
        <>
          <motion.div
            className={cn(
              'bg-background text-foreground-secondary flex min-w-96 max-w-[90%] flex-col gap-4 rounded-xl border p-4 text-sm',
              className,
            )}
            onClick={(e) => e.stopPropagation()}
            {...ModalAnimation}
          >
            {header && (
              <div className={cn('flex select-none items-start justify-between', headerClassName)}>
                <span className={'text-foreground text-md ml-[1px] font-medium'}>{header}</span>
                <CloseButton onClick={close} />
              </div>
            )}
            {children}
          </motion.div>

          {secondary && (
            <motion.div
              className={cn(
                'bg-background text-foreground-secondary flex min-w-96 max-w-[90%] flex-col gap-4 rounded-3xl p-4 text-sm',
                secondaryClassName ?? className,
              )}
              style={{ marginTop: secondaryOffsetPx } as React.CSSProperties}
              onClick={(e) => e.stopPropagation()}
              {...ModalAnimation}
            >
              {secondaryHeader && (
                <div className={cn('flex select-none items-start justify-between', secondaryHeaderClassName)}>
                  {typeof secondaryHeader === 'string' ? (
                    <span className={'text-light text-md ml-[1px] font-medium'}>{secondaryHeader}</span>
                  ) : (
                    secondaryHeader
                  )}
                </div>
              )}
              {secondary}
            </motion.div>
          )}
        </>
      </motion.div>
    </Portal>
  )
}

export const OverlayAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.1 },
}

export const ModalAnimation = {
  initial: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
  transition: { duration: 0.1 },
}
